import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import ModalContext, { ModalConfig } from './ModalContext';
import ModalContentContext from './ModalContentContext';
import { ModalComponent } from './types';
import { AnimatePresence } from 'motion/react';
import Button from '../Button/Button';
import css from './modal.module.scss';
import Icon from '../Icon/Icon';
import { useForceUpdate } from 'utils/use-force-update';

const ModalProvider = ({ children }: React.PropsWithChildren) => {
  const [stack, setStack] = useState<ModalComponent[]>([]);
  const extraProps = useRef(new WeakMap());
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const forceUpdate = useForceUpdate();

  const value = useMemo(() => {
    const open = (component: ModalComponent, props: ModalConfig = {}) => {
      extraProps.current.set(component, props);
      setStack((prev) => [...prev, component]);

      return () => close(component);
    };

    const close = (component: ModalComponent) => {
      extraProps.current.delete(component);
      setStack(stack.filter((item) => item !== component));
    };

    const closeAll = () => {
      setStack([]);
      extraProps.current = new WeakMap();
    };

    return {
      open,
      close,
      closeAll,
    };
  }, [stack, setStack]);

  const contentContext = useMemo(
    () => ({
      scrollToTop: () => {
        contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      },
      bodyRootRef: contentRef,
      isBusy,
      setIsBusy,
    }),
    [isBusy],
  );

  useEffect(() => {
    if (stack.length === 0) {
      setIsBusy(false);
    }
  }, [stack.length]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {stack.length > 0 &&
          stack.map((Component, index) => {
            const props = extraProps.current.get(Component) || {};
            const { header, footer, title, onBeforeClose, ...modalProps } =
              props;
            const hasTitle = title != null && title !== '';
            const showHeader = hasTitle || header != null;

            const closeModal = () => {
              onBeforeClose?.();
              value.close(Component);
              props.onClose?.();
            };

            const update = (nextProps: ModalConfig) => {
              const current = extraProps.current.get(Component) || {};
              extraProps.current.set(Component, {
                ...current,
                ...nextProps,
              });
              forceUpdate();
            };

            return (
              <Modal
                key={index}
                {...modalProps}
                open
                onClose={showHeader ? undefined : closeModal}
              >
                <ModalContentContext.Provider value={contentContext}>
                  <Modal.Content>
                    {showHeader ? (
                      <Modal.Header>
                        {hasTitle ? <Modal.Title>{title}</Modal.Title> : null}
                        {header
                          ? React.createElement(header, { close: closeModal })
                          : null}
                        {!props.disableClose && (
                          <Button
                            onClick={closeModal}
                            className={css['close-button-header']}
                            startIcon={<Icon name={'fas fa-times'} />}
                            variant={'text'}
                          />
                        )}
                      </Modal.Header>
                    ) : null}
                    <Modal.Body>
                      <Component close={closeModal} update={update} />
                    </Modal.Body>
                    {footer ? (
                      <Modal.Footer>
                        {React.createElement(footer, { close: closeModal })}
                      </Modal.Footer>
                    ) : null}
                  </Modal.Content>
                </ModalContentContext.Provider>
              </Modal>
            );
          })}
      </AnimatePresence>
    </ModalContext.Provider>
  );
};

export default ModalProvider;
